import type {
  InvoiceData,
  InvoiceTag,
  LineItem,
  Supplier,
} from "@/apis/gql/types";
import { BU_JUSTIFICATIONS } from "@/components/shared/form/lineItems/taxCode";
import { Tags, tagNameToColor } from "@/lib/miscellaneous/tags";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertTriangle, Clock, Files, Sparkles } from "lucide-react";

import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  InvoiceSmbReviewStatus,
  InvoiceStatus,
} from "@/lib/constants/invoiceStatus";

// Define a minimal type constraint for the required row shape
type CreditorServiceRow = {
  invoice: Pick<
    InvoiceData,
    | "isDuplicate"
    | "isPaid"
    | "smbReviewStatus"
    | "aiSummary"
    | "tags"
    | "status"
  >;
  lineItems: Pick<LineItem, "lineItemId" | "buJustification">[];
  supplier?: Pick<Supplier, "supplierName">;
  aiSummary?: string;
  tags?: InvoiceTag[];
};

const creditorServiceColumn = <T extends CreditorServiceRow>(
  showAttentionIcon = false,
): ColumnDef<T> => {
  return {
    id: "supplierName",
    accessorKey: "supplier.supplierName",
    size: 250,
    header: (header) => {
      return (
        <DataTableColumnHeader
          column={header.column}
          title="Kreditor / Leistung"
        />
      );
    },
    cell: (cell) => {
      const invoice = cell.row.original.invoice;
      const lineItems = cell.row.original.lineItems;
      const isProcessing = invoice.status === InvoiceStatus.NEW;
      const supplierName = cell.row.original.supplier?.supplierName;
      const leistung = invoice.aiSummary || "Unbekannt";
      const tags = cell.row.original.tags || [];

      if (isProcessing) {
        return (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-gray-500" />
            <span className="text-sm">KI-Verarbeitung</span>
          </div>
        );
      }

      return (
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            {supplierName && (
              <span
                className="font-medium leading-tight overflow-hidden whitespace-nowrap text-ellipsis"
                title={supplierName}
              >
                {supplierName}
              </span>
            )}
            <span className="text-muted-foreground text-sm leading-tight overflow-hidden whitespace-nowrap text-ellipsis">
              {leistung}
            </span>
          </div>
          {showAttentionIcon && (
            <AttentionIndicator
              invoice={invoice}
              lineItems={lineItems}
              tags={tags}
            />
          )}
        </div>
      );
    },
  };
};

interface AttentionIndicatorProps {
  invoice: Pick<
    InvoiceData,
    "isDuplicate" | "isPaid" | "smbReviewStatus" | "aiSummary" | "tags"
  >;
  lineItems: Pick<LineItem, "lineItemId" | "buJustification">[];
  tags: InvoiceTag[];
}

function AttentionIndicator({
  invoice,
  lineItems,
  tags,
}: AttentionIndicatorProps) {
  const hasIncorrectVat = lineItems.some(
    (lineItem) => lineItem.buJustification === BU_JUSTIFICATIONS.B2C,
  );

  const needsAddressCheck = tags.some(
    (tag) => tag.tagName === Tags.AdressatPruefen,
  );

  const hasDuplicate = invoice.isDuplicate;

  const isAutoBooking =
    invoice.smbReviewStatus === InvoiceSmbReviewStatus.REVIEWED_ZERO_TOUCH;

  if (!needsAddressCheck && !hasIncorrectVat && !hasDuplicate && !isAutoBooking)
    return null;

  let Icon = AlertTriangle;
  let color = tagNameToColor(Tags.AdressatPruefen); // Default color for UStIdFehlt/AdressatPruefen
  let tooltipText = {
    title: "Empfängerangaben prüfen",
    description: "Bitte öffne den Beleg.",
  };

  if (hasDuplicate) {
    Icon = Files;
    color = tagNameToColor(Tags.Duplikat);
    tooltipText = {
      title: "Duplikat",
      description: "Diesen Beleg kannst du höchstwahrscheinlich löschen.",
    };
  } else if (needsAddressCheck || hasIncorrectVat) {
    Icon = AlertTriangle;
    color = tagNameToColor(Tags.AdressatPruefen); // Default color for UStIdFehlt/AdressatPruefen
    tooltipText = {
      title: "Empfängerangaben prüfen",
      description: "Bitte öffne den Beleg.",
    };
  } else if (isAutoBooking) {
    Icon = Sparkles;
    color = {
      backgroundColor: "#ecfef2",
      textColor: "#259834",
    };
    tooltipText = {
      title: "Auto-Beleg",
      description:
        "Dieser Beleg wurde automatisch als “geprüft” markiert. Du musst nichts mehr machen.",
    };
  }

  return (
    <div className="flex-shrink-0">
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <div
              className="w-9 h-9 border-2 rounded-full flex items-center justify-center cursor-pointer"
              style={{
                backgroundColor: color.backgroundColor,
                borderColor: color.textColor,
              }}
            >
              <Icon className="h-5 w-5" style={{ color: color.textColor }} />
            </div>
          </TooltipTrigger>
          <TooltipContent
            sideOffset={8}
            className="bg-white text-gray-700 border border-gray-200 shadow-lg rounded-lg px-4 py-2 max-w-[330px]"
          >
            <div className="flex flex-col gap-1">
              <span className="font-bold">{tooltipText.title}</span>
              <span className="font-normal">{tooltipText.description}</span>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export default creditorServiceColumn;
